/**
 * Instant Coverage A/B test — engine for the instant arm, loaded once from the
 * head bootstrap. Visibility is pure CSS on the `redo-instant` html class.
 */
(function () {
  "use strict";
  if (window.__redoInstantEngine) return;
  window.__redoInstantEngine = true;
  if (window.__redoInstantArm !== "instant") return;

  var NAV_TIMEOUT_MS = 4000;
  var PENDING_RESET_MS = 2000;

  function postJson(url, body) {
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then(function (r) {
      if (!r.ok) throw new Error(url + " -> " + r.status);
      return r.json();
    });
  }

  function coverageAttributes(wrap, optedIn) {
    return {
      redo_treatment_id: wrap.getAttribute("data-treatment-id"),
      redo_opted_in_from_cart: optedIn ? "true" : "false",
      redo_metadata: JSON.stringify({ optedTimestamp: Date.now() }),
    };
  }

  function toggleModal(open) {
    var modal = document.querySelector("[data-redo-instant-modal]");
    if (!modal) return;
    if (open) {
      if (modal.showModal) modal.showModal();
      else modal.setAttribute("open", "");
    } else if (modal.close) {
      modal.close();
    } else {
      modal.removeAttribute("open");
    }
  }

  document.addEventListener("click", function (ev) {
    if (!ev.target || !ev.target.closest) return;
    if (ev.target.closest("[data-redo-instant-info]")) {
      ev.preventDefault();
      toggleModal(true);
    } else if (
      ev.target.closest("[data-redo-instant-modal-close]") ||
      ev.target.hasAttribute("data-redo-instant-modal")
    ) {
      // The dialog element itself is the backdrop area around the content.
      toggleModal(false);
    }
  });

  /** Redo's buttons show a pending state and re-enable after ~2s, so an upsell
   *  popup (which keeps the shopper on the page) can't leave ours stuck. */
  function setPending(btn, on) {
    if (!btn) return;
    if (on) {
      btn.classList.add("redo-instant--pending");
      btn.setAttribute("aria-busy", "true");
      if (!btn.querySelector(".redo-instant__spinner")) {
        var sp = document.createElement("span");
        sp.className = "redo-instant__spinner";
        btn.appendChild(sp);
      }
    } else {
      btn.classList.remove("redo-instant--pending");
      btn.removeAttribute("aria-busy");
      var el = btn.querySelector(".redo-instant__spinner");
      if (el) el.remove();
    }
  }

  function anyPending() {
    return !!document.querySelector(".redo-instant--pending");
  }

  /** Clicking the merchant's own button keeps their checkout behaviour — upsell
   *  popups, validation, analytics — instead of jumping straight to /checkout. */
  function proceedToCheckout(wrap) {
    if (wrap.getAttribute("data-click-native") !== "true") {
      window.location.href = "/checkout";
      return;
    }
    var selector = wrap.getAttribute("data-native-click-selector");
    var nativeEl = selector ? document.querySelector(selector) : null;
    if (!nativeEl) {
      nativeEl = wrap.querySelector(
        ".redo-instant__native button, .redo-instant__native [type=submit], .redo-instant__native a"
      );
    }
    if (!nativeEl) {
      window.location.href = "/checkout";
      return;
    }
    // Un-hide first: apps can ignore clicks on elements that aren't visible.
    wrap.classList.add("redo-instant--handoff");
    nativeEl.click();
  }

  /** Same test the app uses (isCoverageFeeProduct): our own product first, then
   *  the vendor/sku/title heuristics that catch coverage lines from a split
   *  product or an older Redo setup. */
  function isRedoLine(item, productId, productHandle) {
    if (productId && Number(item.product_id) === productId) return true;
    if (productHandle && item.handle === productHandle) return true;
    var vendor = String(item.vendor || "").toLowerCase().trim();
    var sku = String(item.sku || "").toLowerCase();
    var title = String(item.product_title || item.title || "").toLowerCase();
    return (
      /^re:?do$/.test(vendor) ||
      sku.indexOf("x-redo") === 0 ||
      title.indexOf("re:do") !== -1 ||
      title.indexOf("redo fee") !== -1
    );
  }

  /** The cart must end up with exactly one Redo line — ours, quantity 1. Stale
   *  lines (a variant Redo priced for a different cart total, a leftover from a
   *  split product) are removed, matching the widget's own reconciliation. */
  function reconcileCart(cart, optedIn, productId, productHandle, variantId, attributes) {
    var keep = null;
    var drop = [];
    var merchantItems = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (!isRedoLine(item, productId, productHandle)) {
        merchantItems++;
      } else if (!keep && item.id === variantId) {
        keep = item;
      } else {
        drop.push(item);
      }
    }

    // Stale lines go by line key — variant-keyed updates can't tell two lines
    // of the same variant apart.
    var ops = drop.map(function (item) {
      return function () { return postJson("/cart/change.js", { id: item.key, quantity: 0 }); };
    });

    // One write carries the quantity change and the attributes, saving a round
    // trip in front of checkout.
    var qty = null;
    if (optedIn && merchantItems > 0) {
      if (!keep || keep.quantity !== 1) qty = 1;
    } else if (keep) {
      qty = 0;
    }
    var body = { attributes: attributes };
    if (qty !== null) {
      body.updates = {};
      body.updates[variantId] = qty;
    }
    ops.push(function () { return postJson("/cart/update.js", body); });

    // Sequential: Shopify serialises cart writes and rejects overlapping ones.
    return ops.reduce(function (p, op) { return p.then(op); }, Promise.resolve());
  }

  function handleCheckoutClick(wrap, optedIn, onDone) {
    var variantId = Number(wrap.getAttribute("data-variant-id"));
    var productId = Number(wrap.getAttribute("data-product-id"));
    var productHandle = wrap.getAttribute("data-product-handle");
    var done = false;
    function go() {
      if (done) return;
      done = true;
      proceedToCheckout(wrap);
      if (onDone) onDone();
    }
    // Never trap the shopper behind a failed/slow call.
    setTimeout(go, NAV_TIMEOUT_MS);

    fetch("/cart.js")
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        return reconcileCart(cart, optedIn, productId, productHandle, variantId,
                             coverageAttributes(wrap, optedIn));
      })
      .then(go)
      .catch(go);
  }

  document.addEventListener(
    "click",
    function (ev) {
      var btn = ev.target && ev.target.closest && ev.target.closest("[data-redo-instant-action]");
      if (!btn) return;
      ev.preventDefault();
      ev.stopPropagation();
      if (anyPending()) return;
      try {
        var wrap = btn.closest("[data-redo-instant]");
        setPending(btn, true);
        handleCheckoutClick(wrap, btn.getAttribute("data-redo-instant-action") === "with", function () {
          setTimeout(function () { setPending(btn, false); }, PENDING_RESET_MS);
        });
      } catch (e) {
        // Fail open: restore the native button and let the shopper through.
        document.documentElement.classList.remove("redo-instant");
        window.location.href = "/checkout";
      }
    },
    true
  );
})();
